import {
  Stack,
  Avatar,
  TableRow,
  MenuItem,
  TableCell,
  Typography,
  IconButton,
  ListItemText,
  CircularProgress,
} from '@mui/material';

import Label from 'src/components/label';
import { useState } from 'react';
import { useBoolean } from 'src/hooks/use-boolean';
import { PositionTxType } from 'src/type/position';
import { TransactionType, TransactionStatus } from 'src/type/transaction';
import { fNumber } from 'src/utils/format-number';
import { fDate, fTime } from 'src/utils/format-time';
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { BranchType } from 'src/type/branch';
import axios, { endpoints } from 'src/utils/axios';
import { enqueueSnackbar } from 'notistack';
import { convertGold } from 'src/utils/convert';
import { downloadPublicFile } from 'src/utils/download-file';
import useCurrentDateTime from 'src/hooks/use-current-date-time';
import SellApproveDialog from './sell-position-approve-dialog';
import RejectDialog from './position-reject-dialog';
import BuyApproveDialog from './buy-position-approve-dialog';
import PositionViewDialog from './position-view-dialog';
import CancelDialog from './position-cancel-dialog';
import PositionUpdatePaymentDialog from './position-update-payment-dialog';
import PositionUpdateNoteDialog from './position-update-note-dialog';
import { txStatusLabel } from './utils';

type Props = {
  row: PositionTxType;
  handleReload: () => void;
  branches: BranchType[];
};

export default function PositionTableRow({ row, handleReload, branches }: Props) {
  const popover = usePopover();
  const sellApprove = useBoolean();
  const buyApprove = useBoolean();
  const cancel = useBoolean();
  const reject = useBoolean();
  const view = useBoolean();
  const updatePayment = useBoolean();
  const updateNote = useBoolean();

  const sending = useBoolean(false);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);

  const {
    product,
    createdAt,
    type,
    status,
    amount,
    user,
    unitName,
    paymentVoucherNo,
    paymentReceiptNo,
    depositReceiptNo,
    redeemReceiptNo,
    paymentVoucherUrl,
    paymentReceiptUrl,
    depositReceiptUrl,
    redeemReceiptUrl,
  } = row;

  const txNo = paymentVoucherNo || paymentReceiptNo || depositReceiptNo || redeemReceiptNo;

  const hasDocumentUrls = !!(
    paymentVoucherUrl ||
    paymentReceiptUrl ||
    depositReceiptUrl ||
    redeemReceiptUrl
  );

  const isOrnament = product.isOrnament || false;

  const handleSendPdf = async () => {
    try {
      sending.onTrue();
      setLoadingMessage('กำลังส่งเอกสาร');
      await axios.get(endpoints.sendPdf.get(row.id));

      // await new Promise((resolve) => setTimeout(resolve, 5000));

      enqueueSnackbar('ส่งเอกสารถึงลูกค้าเรียบร้อย', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar('ส่งเอกสารถึงลูกค้าไม่สำเร็จ', {
        variant: 'error',
      });
    } finally {
      popover.onClose();
      sending.onFalse();
      setLoadingMessage(null);
    }
  };

  const currentDateTime = useCurrentDateTime();

  const handleCancelWaitingPayment = async () => {
    try {
      sending.onTrue();
      setLoadingMessage('กำลังยกเลิกรายการ');
      await axios.post(endpoints.transactions.position.cancelBuyPosition(row.id), {
        currentDateTime,
      });
      enqueueSnackbar('ยกเลิกรายการสำเร็จ', { variant: 'success' });
      handleReload();
    } catch (error) {
      enqueueSnackbar('ยกเลิกรายการไม่สำเร็จ', { variant: 'error' });
    } finally {
      popover.onClose();
      sending.onFalse();
      setLoadingMessage(null);
    }
  };

  const isBuy = type === TransactionType.BUY_POSITION;

  const handleDownloadPdf = async () => {
    try {
      const url = isBuy ? row.paymentReceiptUrl : row.paymentVoucherUrl;
      const no = isBuy ? row.paymentReceiptNo : row.paymentVoucherNo;
      const prefix = isBuy ? 'buy-position' : 'sell-position';

      if (url) {
        await downloadPublicFile(url, `${prefix}-${no || row.id}.pdf`);
      } else {
        enqueueSnackbar('ไม่พบเอกสาร', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('ดาวน์โหลดเอกสารไม่สำเร็จ', { variant: 'error' });
    } finally {
      popover.onClose();
    }
  };

  return (
    <>
      <TableRow>
        <TableCell padding="checkbox" align="center">
          {isBuy ? (
            <img src="/assets/icons/common/ic-add-square.svg" alt="" />
          ) : (
            <img src="/assets/icons/common/ic-minus-square.svg" alt="" />
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row" alignItems="center">
            <Avatar alt="" sx={{ mr: 2 }}>
              {user?.firstName?.[0].toUpperCase()}
            </Avatar>

            <ListItemText
              disableTypography
              primary={
                <Typography variant="body2" noWrap>
                  {user?.firstName} {user?.lastName}
                </Typography>
              }
              secondary={
                <Typography variant="caption" color="text.secondary" noWrap>
                  #{txNo || '-'}
                </Typography>
              }
            />
          </Stack>
        </TableCell>

        <TableCell>
          <Stack direction="row" alignItems="center">
            <Avatar alt="" sx={{ mr: 2 }} src={product.imageUrl}>
              {product.name.charAt(0)}
            </Avatar>

            <ListItemText
              disableTypography
              primary={
                <Typography variant="body2" noWrap>
                  {product.name}
                </Typography>
              }
              // sx={{ height: 44 }}
            />
          </Stack>
        </TableCell>

        <TableCell>
          {/* <Typography
            variant="subtitle1"
            color={type === TransactionType.BUY_POSITION ? 'success.main' : 'error.main'}
          >
            {fNumber(amount)} {unitName}
          </Typography> */}

          <ListItemText
            primary={`${isBuy ? 'ซื้อ' : 'ขาย'} ${fNumber(amount)} ${unitName || 'บาท'}`}
            secondary={`≈ ${fNumber(convertGold(amount, unitName === 'กรัม' ? 'weightBaht' : 'weightGrams', isOrnament))} ${unitName === 'กรัม' ? 'บาท' : 'กรัม'}`}
            primaryTypographyProps={{
              typography: 'subtitle1',
              noWrap: true,
              color: type === TransactionType.BUY_POSITION ? 'success.main' : 'error.main',
            }}
            secondaryTypographyProps={{
              mt: 0.5,
              component: 'span',
              typography: 'caption',
              noWrap: true,
            }}
          />
        </TableCell>

        <TableCell>
          <ListItemText
            primary={fDate(createdAt, 'DD MMMM BBBB')}
            secondary={fTime(createdAt)}
            primaryTypographyProps={{ typography: 'body2', noWrap: true }}
            secondaryTypographyProps={{
              mt: 0.5,
              component: 'span',
              typography: 'caption',
            }}
          />
        </TableCell>

        <TableCell>
          <Label
            variant="soft"
            color={
              (status === TransactionStatus.SUCCESS && 'success') ||
              (status === TransactionStatus.PENDING && 'warning') ||
              (status === TransactionStatus.REJECTED && 'error') ||
              (status === TransactionStatus.CANCEL && 'error') ||
              (status === TransactionStatus.WAITING && 'info') ||
              (status === TransactionStatus.WAITING_TICKET && 'secondary') ||
              'default'
            }
          >
            {txStatusLabel(status)}
          </Label>
        </TableCell>

        {/* <TableCell>
          {status === TransactionStatus.SUCCESS || status === TransactionStatus.REJECTED ? (
            <ListItemText
              primary={fDate(updatedAt, 'DD MMMM BBBB')}
              secondary={fTime(updatedAt)}
              primaryTypographyProps={{ typography: 'body2', noWrap: true }}
              secondaryTypographyProps={{
                mt: 0.5,
                component: 'span',
                typography: 'caption',
              }}
            />
          ) : (
            '-'
          )}
        </TableCell> */}

        <TableCell>
          <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={sending.value ? undefined : popover.onClose}
        arrow="top-right"
        sx={{ width: 160 }}
      >
        {sending.value ? (
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} p={1}>
            <CircularProgress size={20} />
            <Typography variant="caption">{loadingMessage || 'กำลังดำเนินการ'}</Typography>
          </Stack>
        ) : (
          <>
            {(status === TransactionStatus.PENDING ||
              status === TransactionStatus.WAITING_TICKET) && (
              <>
                <MenuItem
                  onClick={() => {
                    popover.onClose();
                    if (type === TransactionType.SELL_POSITION) {
                      sellApprove.onTrue();
                    } else {
                      buyApprove.onTrue();
                    }
                  }}
                  sx={{ color: 'success.main' }}
                >
                  <Iconify icon="solar:check-square-bold" />
                  อนุมัติ
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    popover.onClose();
                    reject.onTrue();
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <Iconify icon="solar:close-square-bold" />
                  ปฏิเสธ
                </MenuItem>
              </>
            )}

            {status === TransactionStatus.WAITING && (
              <MenuItem
                onClick={() => {
                  popover.onClose();
                  cancel.onTrue();
                }}
                sx={{ color: 'error.main' }}
              >
                <Iconify icon="solar:close-square-bold" />
                ยกเลิกรายการ (waiting)
              </MenuItem>
            )}

            {status === TransactionStatus.WAITING_PAYMENT && (
              <MenuItem
                onClick={() => {
                  popover.onClose();
                  updatePayment.onTrue();
                }}
                sx={{ color: 'info.main' }}
              >
                <Iconify icon="solar:pen-new-square-bold" />
                อัพเดท
              </MenuItem>
            )}

            {status === TransactionStatus.WAITING_PAYMENT && (
              <MenuItem onClick={handleCancelWaitingPayment} sx={{ color: 'error.main' }}>
                <Iconify icon="solar:close-square-bold" />
                ยกเลิกรายการ
              </MenuItem>
            )}

            {status === TransactionStatus.SUCCESS && (
              <MenuItem
                onClick={() => {
                  popover.onClose();
                  updateNote.onTrue();
                }}
              >
                <Iconify icon="solar:pen-new-square-bold" />
                แก้ไขข้อมูล
              </MenuItem>
            )}

            <MenuItem
              onClick={() => {
                popover.onClose();
                view.onTrue();
              }}
            >
              <Iconify icon="solar:info-square-bold" />
              รายละเอียด
            </MenuItem>

            {status === TransactionStatus.SUCCESS &&
              (hasDocumentUrls ? (
                <>
                  <MenuItem onClick={handleSendPdf}>
                    <Iconify icon="solar:letter-bold" />
                    ส่งเอกสาร
                  </MenuItem>

                  <MenuItem onClick={handleDownloadPdf}>
                    <Iconify icon="solar:download-bold" />
                    ดาวน์โหลด PDF
                  </MenuItem>
                </>
              ) : (
                <MenuItem disabled sx={{ color: 'warning.main' }}>
                  <Iconify icon="solar:document-text-bold" />
                  รอเอกสาร
                </MenuItem>
              ))}
          </>
        )}
      </CustomPopover>

      {buyApprove.value && (
        <BuyApproveDialog
          open={buyApprove.value}
          onClose={buyApprove.onFalse}
          handleReload={handleReload}
          tx={row}
          branches={branches}
        />
      )}

      {sellApprove.value && (
        <SellApproveDialog
          open={sellApprove.value}
          onClose={sellApprove.onFalse}
          handleReload={handleReload}
          tx={row}
          branches={branches}
        />
      )}

      {reject.value && (
        <RejectDialog
          open={reject.value}
          onClose={reject.onFalse}
          handleReload={handleReload}
          tx={row}
        />
      )}

      {cancel.value && (
        <CancelDialog
          open={cancel.value}
          onClose={cancel.onFalse}
          handleReload={handleReload}
          tx={row}
        />
      )}

      {view.value && <PositionViewDialog open={view.value} onClose={view.onFalse} tx={row} />}

      {updatePayment.value && (
        <PositionUpdatePaymentDialog
          open={updatePayment.value}
          onClose={updatePayment.onFalse}
          tx={row}
          handleReload={handleReload}
        />
      )}

      {updateNote.value && (
        <PositionUpdateNoteDialog
          open={updateNote.value}
          onClose={updateNote.onFalse}
          tx={row}
          handleReload={handleReload}
        />
      )}
    </>
  );
}
